import { CompanyUserAvatar } from "~/components/user/company-user-avatar";
import { useInternalShareDropTarget } from "~/hooks/use-internal-share-drop-target";
import { getTeamAvatarStyle } from "~/lib/team-colors";
import { isInternalShareDragData } from "~/lib/internal-share-drag";
import { presenceDotClass } from "~/lib/presence";
import { cn } from "~/lib/utils";
import type { WorkspaceChannel, WorkspaceDirectMessage, WorkspaceTeam } from "~/types/workspace";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { Hash } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type InternalShareDropTrayProps = {
  members: WorkspaceDirectMessage[];
  channels?: WorkspaceChannel[];
  teams?: WorkspaceTeam[];
};

type ShareableTeam = WorkspaceTeam & { conversationId: number };

function UserDropTarget({ member }: { member: WorkspaceDirectMessage }) {
  const { ref, isDraggedOver } = useInternalShareDropTarget<HTMLDivElement>({
    type: "user",
    companyUserId: member.companyUserId,
  });

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm transition",
        isDraggedOver ? "border-primary bg-primary/10 text-primary" : "border-border bg-background",
      )}
    >
      <span className="relative size-7 shrink-0">
        <CompanyUserAvatar
          companyUserId={member.companyUserId}
          userId={member.id}
          name={member.name}
          avatar={member.avatar}
          className="size-7 text-[11px]"
        />
        <span
          className={cn(
            "border-background absolute -right-0.5 -bottom-0.5 size-3 rounded-full border-2",
            presenceDotClass(member.presenceStatus),
          )}
          aria-hidden="true"
        />
      </span>
      <span className="min-w-0 flex-1 truncate">{member.name}</span>
    </div>
  );
}

function ChannelDropTarget({ channel }: { channel: WorkspaceChannel }) {
  const { ref, isDraggedOver } = useInternalShareDropTarget<HTMLDivElement>({
    type: "channel",
    conversationId: channel.conversationId,
  });

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm transition",
        isDraggedOver ? "border-primary bg-primary/10 text-primary" : "border-border bg-background",
      )}
    >
      <span className="bg-muted text-muted-foreground grid size-7 shrink-0 place-items-center rounded-md">
        <Hash className="size-4" />
      </span>
      <span className="min-w-0 flex-1 truncate">{channel.name}</span>
    </div>
  );
}

function TeamDropTarget({ team }: { team: ShareableTeam }) {
  const { ref, isDraggedOver } = useInternalShareDropTarget<HTMLDivElement>({
    type: "team",
    conversationId: team.conversationId,
  });

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm transition",
        isDraggedOver ? "border-primary bg-primary/10 text-primary" : "border-border bg-background",
      )}
    >
      <span
        className="grid size-7 shrink-0 place-items-center rounded-md text-[11px] font-bold"
        style={getTeamAvatarStyle(team)}
      >
        {team.name.slice(0, 2).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1 truncate">{team.name}</span>
    </div>
  );
}

export function InternalShareDropTray({
  members,
  channels = [],
  teams = [],
}: InternalShareDropTrayProps) {
  const { t } = useTranslation("home_common");
  const [isDragActive, setIsDragActive] = useState(false);
  const shareableTeams = teams.filter(
    (team): team is ShareableTeam => team.conversationId !== null,
  );

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => isInternalShareDragData(source.data),
      onDragStart: () => setIsDragActive(true),
      onDrop: () => setIsDragActive(false),
    });
  }, []);

  if (
    !isDragActive ||
    (members.length === 0 && channels.length === 0 && shareableTeams.length === 0)
  ) {
    return null;
  }

  return (
    <div className="fixed inset-y-0 right-0 z-[60] hidden w-[18rem] max-w-[80vw] p-2 lg:block">
      <div className="bg-background/95 flex h-full flex-col overflow-hidden rounded-2xl border shadow-lg backdrop-blur">
        <div className="text-muted-foreground shrink-0 border-b px-4 py-3 text-[11px] font-semibold tracking-wide uppercase">
          {t("workspace.share_drop_title", "Drop on a user, channel or team to share")}
        </div>
        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 py-3">
          {shareableTeams.length > 0 && (
            <div className="text-muted-foreground px-1 pt-1 pb-0.5 text-[10px] font-semibold tracking-wide uppercase">
              {t("workspace.teams", "Teams")}
            </div>
          )}
          {shareableTeams.map((team) => (
            <TeamDropTarget key={`team-${team.id}`} team={team} />
          ))}
          {channels.length > 0 && (
            <div
              className={cn(
                "text-muted-foreground px-1 pb-0.5 text-[10px] font-semibold tracking-wide uppercase",
                shareableTeams.length > 0 ? "pt-2" : "pt-1",
              )}
            >
              {t("workspace.internal_channels", "Channels")}
            </div>
          )}
          {channels.map((channel) => (
            <ChannelDropTarget key={`channel-${channel.conversationId}`} channel={channel} />
          ))}
          {members.length > 0 && (
            <div className="text-muted-foreground px-1 pt-2 pb-0.5 text-[10px] font-semibold tracking-wide uppercase">
              {t("workspace.direct_messages", "Direct messages")}
            </div>
          )}
          {members.map((member) => (
            <UserDropTarget key={`user-${member.companyUserId}`} member={member} />
          ))}
        </div>
      </div>
    </div>
  );
}
